import { useMemo, useState } from 'react';
import { Button, Field, Select } from '@bb/ui';
import { ApiError } from '../../../api/client.ts';
import { issuesAt, type ActionBasedIssue } from '../trigger/actionBased.ts';
import { NumberInput } from '../trigger/NumberInput.tsx';
import { CustomEventFields } from '../trigger/TriggerEditor.tsx';
import {
  conversionIssueList,
  CONVERSIONS_MAX,
  makePrimary,
  newConversion,
  WINDOW_DEFAULT_DAYS,
  WINDOW_MAX_DAYS,
  WINDOW_UNIT_SECONDS,
  windowSeconds,
  windowUnitOf,
  withConversionDefaults,
  withKind,
  type ConversionEvent,
  type ConversionKind,
  type Conversions,
  type WindowUnit,
} from './conversions.ts';

export { conversionsIssues } from './conversions.ts';
export type { Conversions } from './conversions.ts';

export interface ConversionsStepProps {
  environmentId: string;
  /** `content.conversions` of the draft; nothing stored reads as none, and then the default conversion applies. */
  value: Conversions | undefined;
  /** Every edit, valid or not: the step shows what the contract rejects (see `conversionsIssues`). */
  onChange: (next: Conversions) => void;
  /** The campaign has been launched: its conversion events are fixed and nothing can change. */
  locked: boolean;
  /** «Duplicate to change»: copies the campaign so the copy can get other conversion events. Not offered without it. */
  onDuplicate?: () => void;
  /** The last failed draft save; `conversions_locked` (409, the campaign was launched meanwhile) is explained. */
  saveError?: unknown;
}

const KINDS: Array<{ value: ConversionKind; label: string }> = [
  { value: 'custom_event', label: 'Performs a custom event' },
  { value: 'session_start', label: 'Starts a session' },
];
const UNITS: Array<{ value: WindowUnit; label: string }> = [
  { value: 'hours', label: 'hours' },
  { value: 'days', label: 'days' },
];

function Errors({ messages }: { messages: string[] }) {
  return (
    <>
      {messages.map((message) => (
        <p key={message} className="bb-field__error" role="alert">
          {message}
        </p>
      ))}
    </>
  );
}

/** «Within N hours / days»: the unit stays as chosen, the window is stored in seconds; empty or zero is no window yet (`NaN`). */
function ConversionWindow({ seconds, error, locked, onChange }: { seconds: number; error: string | undefined; locked: boolean; onChange: (seconds: number) => void }) {
  const [unit, setUnit] = useState<WindowUnit>(() => windowUnitOf(seconds));
  const amount = Number.isFinite(seconds) && seconds > 0 ? seconds / WINDOW_UNIT_SECONDS[unit] : undefined;
  const emit = (n: number | undefined, u: WindowUnit) => onChange(n === undefined || n <= 0 ? Number.NaN : windowSeconds(n, u) || Number.NaN);
  return (
    <div className="audience-filter">
      <Field label="Window" error={error}>
        <NumberInput min={1} step={1} value={amount} disabled={locked} onValueChange={(n) => emit(n, unit)} />
      </Field>
      <Field label="Unit">
        <Select
          options={UNITS}
          value={unit}
          disabled={locked}
          onChange={(e) => {
            const next = e.target.value as WindowUnit;
            setUnit(next);
            if (amount !== undefined) emit(amount, next);
          }}
        />
      </Field>
    </div>
  );
}

interface ItemProps {
  environmentId: string;
  conversion: ConversionEvent;
  index: number;
  issues: readonly ActionBasedIssue[];
  locked: boolean;
  onChange: (next: ConversionEvent) => void;
  onMakePrimary: () => void;
  onRemove: () => void;
}

/** One conversion event: what the user does and the window it is counted in. */
function ConversionItem({ environmentId, conversion, index, issues, locked, onChange, onMakePrimary, onRemove }: ItemProps) {
  const primary = index === 0;
  const label = primary ? 'Primary conversion event' : `Conversion event ${index + 1}`;
  const err = (field: string) => issuesAt(issues, index, field)[0];
  return (
    <fieldset className="audience-group">
      <legend>{label}</legend>
      {primary && <p className="hint">The primary conversion leads the campaign report; variants are compared on it.</p>}
      <Field label="Action" error={err('kind')}>
        <Select options={KINDS} value={conversion.kind} disabled={locked} onChange={(e) => onChange(withKind(conversion, e.target.value as ConversionKind))} />
      </Field>
      {conversion.kind === 'custom_event' && (
        <CustomEventFields
          environmentId={environmentId}
          readOnly={locked}
          issues={issues}
          path={[index]}
          spec={{ kind: 'custom_event', event: conversion.event, propertyFilters: conversion.propertyFilters }}
          onChange={(next) => {
            if (next.kind === 'custom_event') onChange({ ...conversion, event: next.event, propertyFilters: next.propertyFilters });
          }}
        />
      )}
      {conversion.kind === 'session_start' && <p className="hint">A session start in any app of the environment.</p>}
      <ConversionWindow seconds={conversion.windowSeconds} error={err('windowSeconds')} locked={locked} onChange={(seconds) => onChange({ ...conversion, windowSeconds: seconds })} />
      <Errors messages={[...issuesAt(issues, index), ...issuesAt(issues, index, 'id')]} />
      {!locked && (
        <div className="toolbar">
          {!primary && (
            <Button size="sm" aria-label={`Make conversion event ${index + 1} primary`} onClick={onMakePrimary}>
              Make primary
            </Button>
          )}
          <Button size="sm" variant="ghost" aria-label={`Remove ${label.toLowerCase()}`} onClick={onRemove}>
            Remove
          </Button>
        </div>
      )}
    </fieldset>
  );
}

/** What a launched campaign offers instead of editing: an explanation and a copy that can be changed. */
function LockedNotice({ onDuplicate }: { onDuplicate: (() => void) | undefined }) {
  return (
    <div className="notice notice--warning" role="status">
      <p>This campaign has launched: its conversion events cannot change, so its report keeps measuring the same actions. To track other conversion events, duplicate the campaign and launch the copy.</p>
      {onDuplicate && (
        <Button size="sm" onClick={onDuplicate}>
          Duplicate to change
        </Button>
      )}
    </div>
  );
}

/**
 * Conversion events of a campaign (BB-103, AN-003), the «Assign conversions» step of the wizard: up to four actions
 * counted after a user got the campaign, each within its own window; the first is primary. With none, a session start
 * within three days is counted. Controlled: every edit is emitted, and what the contract rejects is shown inline. Once
 * the campaign has launched the conversions are fixed (`locked`); a copy of the campaign can get others.
 */
export function ConversionsStep({ environmentId, value, onChange, locked, onDuplicate, saveError }: ConversionsStepProps) {
  const list = useMemo(() => withConversionDefaults(value), [value]);
  const issues = useMemo(() => conversionIssueList(list), [list]);
  const refusedAsLocked = saveError instanceof ApiError && saveError.code === 'conversions_locked';
  const full = list.length >= CONVERSIONS_MAX;

  return (
    <section className="audience-builder" aria-label="Conversion events">
      <p className="hint">
        Up to {CONVERSIONS_MAX} actions counted after a user got the campaign: from when a push was accepted by the provider or a card or in-app message was seen; for the control group, from when the user was assigned to
        it. Each counts at most once per send, within its own window of up to {WINDOW_MAX_DAYS} days. The first one is primary.
      </p>
      {locked && <LockedNotice onDuplicate={onDuplicate} />}

      {list.length === 0 ? (
        <p className="notice notice--neutral">
          {locked ? 'No conversion events were set' : 'No conversion events yet'}: the campaign counts a session start within {WINDOW_DEFAULT_DAYS} days, the default.
        </p>
      ) : (
        <ol className="plain-list audience-builder">
          {list.map((conversion, index) => (
            // The id keeps each item's window unit through reordering; a duplicate id (refused below) falls back to the position.
            <li key={list.findIndex((c) => c.id === conversion.id) === index ? conversion.id : `${conversion.id}:${index}`}>
              <ConversionItem
                environmentId={environmentId}
                conversion={conversion}
                index={index}
                issues={issues}
                locked={locked}
                onChange={(next) => onChange(list.map((c, i) => (i === index ? next : c)))}
                onMakePrimary={() => onChange(makePrimary(list, index))}
                onRemove={() => onChange(list.filter((_, i) => i !== index))}
              />
            </li>
          ))}
        </ol>
      )}
      <Errors messages={issuesAt(issues)} />

      {!locked && (
        <>
          <Button size="sm" disabled={full} onClick={() => onChange([...list, newConversion()])}>
            Add a conversion event
          </Button>
          {full && <p className="hint">A campaign tracks at most {CONVERSIONS_MAX} conversion events.</p>}
        </>
      )}

      {refusedAsLocked && (
        <div role="alert">
          <p className="bb-field__error">
            The draft was not saved: the campaign has launched, so its conversion events can no longer change. Put them back as they were to save the other changes, or duplicate the campaign to change them.
          </p>
          {!locked && onDuplicate && (
            <Button size="sm" onClick={onDuplicate}>
              Duplicate to change
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
